import { Box, Typography } from '@mui/material';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import TipTapBar from './TipTapBar';
import EditorContainer from './components/EditorContainer';

interface TipTapEditorProps {
  label: string;
  value: string;
  onChange: (data: string) => void;
  error?: boolean;
  helperText?: string;
  maxLength?: number;
  showRemainingChars?: boolean;
}

const TipTapEditor: React.FC<TipTapEditorProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  maxLength,
  showRemainingChars = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          HTMLAttributes: { class: 'BulletList-root' },
        },
      }),
      Placeholder.configure({
        placeholder: 'Enter text...',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'link',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CharacterCount.configure({
        limit: maxLength,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap="8px" width="100%">
      <Typography variant="caption" color={'textPrimary'}>
        {label}
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: '4px',
          '&:focus-within': {
            borderColor: error ? 'error.main' : '',
          },
        }}
      >
        <TipTapBar editor={editor} />
        <EditorContainer>
          <EditorContent editor={editor} />
          {showRemainingChars && (
            <Box position="absolute" bottom={0} right={0} padding="8px 8px 4px">
              <Typography
                variant="body3"
                color={theme => theme.palette.text.primary}
                fontSize="12px"
              >
                {editor.storage.characterCount.characters()}/{maxLength ?? '-'}
              </Typography>
            </Box>
          )}
        </EditorContainer>
      </Box>
      {error && (
        <Typography variant="caption" color="error" margin={'0 14px'}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default TipTapEditor;
